import { ChatBubble } from './ChatBubble.js';
import { groundYFromCentre, perspectiveScale } from './carCatalog.mjs';

const BUFFER_MS = 100;
export const NPC_DISPLAY_H = 240;   // same on-screen height as a player sprite
const LABEL_OFFSET = 150;           // …and the same name tag, both scaled by the scene

// A server-driven NPC: one static texture (`peer.sprite`), flipX by facing, name tag, and the
// same interpolation buffer RemoteAvatar uses. Duck-types RemoteAvatar for remotePresence.
export class NpcAvatar {
  constructor(scene, peer) {
    this.scene = scene;
    this.id = peer.id;
    this.isNpc = true;
    // Outdoors the world is drawn at one real-world scale (see carCatalog's worldScale):
    // an NPC is a person too, so she shrinks exactly as the players do.
    this._worldScale = scene.worldScale ?? 1;
    this._perspective = scene.perspective;
    this._labelOffset = LABEL_OFFSET * this._worldScale;
    const key = scene.textures.exists(peer.sprite) ? peer.sprite : 'natalka_base';
    this.sprite = scene.add.image(peer.x, peer.y, key).setDepth(9);
    const tex = scene.textures.get(key).getSourceImage();
    this._aspect = tex.width / tex.height;
    const h = NPC_DISPLAY_H * this._worldScale;
    this.sprite.setDisplaySize(h * this._aspect, h);
    this.label = scene.add.text(peer.x, peer.y - this._labelOffset, peer.name, {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#3355aacc', padding: { x: 6, y: 2 },
    }).setOrigin(0.5).setDepth(20);
    this._buf = [{ t: performance.now(), x: peer.x, y: peer.y }];
    this._facing = peer.facing === 'left' ? -1 : 1;
    this._bubble = null;
    this._applyFlip();
  }

  get mode() { return 'npc'; }
  setHidden() {}
  setSleeping() {}
  applyOutfit() {}

  pushMove({ x, y, facing }) {
    this._buf.push({ t: performance.now(), x, y });
    if (this._buf.length > 10) this._buf.shift();
    if (facing) this._facing = facing === 'left' ? -1 : 1;
    this._applyFlip();
  }

  _applyFlip() { this.sprite.setFlipX(this._facing === -1); }

  update() {
    const renderT = performance.now() - BUFFER_MS;
    const buf = this._buf;
    let a = buf[0], b = buf[buf.length - 1];
    for (let i = 0; i < buf.length - 1; i++) {
      if (buf[i].t <= renderT && buf[i + 1].t >= renderT) { a = buf[i]; b = buf[i + 1]; break; }
    }
    const span = Math.max(1, b.t - a.t);
    const k = Math.max(0, Math.min(1, (renderT - a.t) / span));
    const x = a.x + (b.x - a.x) * k, y = a.y + (b.y - a.y) * k;
    this.sprite.setPosition(x, y);
    // Outdoors an NPC walks in the same y-perspective everyone else does: she grows as she
    // comes down the screen towards the viewer (see carCatalog's PERSPECTIVE).
    if (this._worldScale !== 1 || this._perspective) {
      const s = this._worldScale * perspectiveScale(this.groundY, this._perspective);
      const h = NPC_DISPLAY_H * s;
      this.sprite.setDisplaySize(h * this._aspect, h);
      this._labelOffset = LABEL_OFFSET * s;
    }
    this.label.setPosition(x, y - this._labelOffset);
  }

  /** Where her feet are — the depth sort's anchor. Undefined indoors (no perspective). */
  get groundY() {
    if (this._worldScale === 1 && !this._perspective) return undefined;
    return groundYFromCentre(NPC_DISPLAY_H * this._worldScale / 2, this.sprite.y, this._perspective);
  }

  setChat(text) {
    this._bubble?.destroy();
    this._bubble = new ChatBubble(this.scene, this.sprite, text);
  }

  destroy() {
    this.sprite.destroy();
    this.label.destroy();
    this._bubble?.destroy?.();
  }
}
